import { SignalFilled } from '@compass/Icon'
import { QueryFilterDefinition, QueryFilterMenuSection, QueryFilterOption } from 'components/QueryFilters'
import { useWorkspaceEnvironments } from 'hooks/api/environment'
import {
  Bot,
  Box,
  Fingerprint,
  Focus,
  Globe,
  GlobeLockIcon,
  Grid2X2,
  IdCardLanyardIcon,
  ServerIcon,
} from 'lucide-react'
import { useMemo, useRef } from 'react'

import { BOT_PROVIDER_DEFINITIONS, KNOWN_BOT_NAMES } from '../consts'
import {
  categoryOptions,
  confidenceOptions,
  identityOptions,
  mergeRememberedOptions,
  mergeSelectedOptions,
  uniqueOptions,
  withBotNameIcons,
  withProviderLabelsAndIcons,
} from '../filterOptions'
import { BotDetectionEvent, BotDetectionEventsFilter } from '../models'

export const BOT_DETECTION_EVENTS_FILTER_KEYS = [
  'botDetected',
  'botName',
  'category',
  'provider',
  'identity',
  'confidence',
  'ipAddress',
  'proxy',
  'vpn',
  'environmentIds',
] as const satisfies readonly (keyof BotDetectionEventsFilter)[]

export type BotDetectionEventsFilterKey = (typeof BOT_DETECTION_EVENTS_FILTER_KEYS)[number]
export type BotDetectionEventsFilterValues = Pick<BotDetectionEventsFilter, BotDetectionEventsFilterKey>

const hasFilterValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.length > 0
  }
  return value !== undefined && value !== ''
}

export function getActiveBotDetectionEventsFilters(
  filterValues: Partial<BotDetectionEventsFilter>
): Partial<BotDetectionEventsFilterValues> {
  return Object.fromEntries(
    BOT_DETECTION_EVENTS_FILTER_KEYS.flatMap((filterKey) => {
      const value = filterValues[filterKey]

      return hasFilterValue(value) ? [[filterKey, value]] : []
    })
  ) as Partial<BotDetectionEventsFilterValues>
}

export function getClearedBotDetectionEventsFilters(
  filterDefinitions: Record<BotDetectionEventsFilterKey, QueryFilterDefinition<BotDetectionEventsFilterValues>>
): Partial<Record<BotDetectionEventsFilterKey, undefined>> {
  return Object.fromEntries(
    Object.values(filterDefinitions).map((filterDefinition) => [filterDefinition.id, undefined])
  )
}

export function getAppliedBotDetectionEventsFilterCount(filterValues: Partial<BotDetectionEventsFilterValues>) {
  return Object.values(filterValues).filter(hasFilterValue).length
}

export function useBotDetectionEventsFilter({
  subscriptionId,
  events,
  filterValues,
}: {
  subscriptionId: string
  events: BotDetectionEvent[]
  filterValues: Partial<BotDetectionEventsFilterValues>
}) {
  const { data: environments } = useWorkspaceEnvironments(subscriptionId)
  const environmentOptions = useMemo(
    () => (environments ?? []).map((environment) => ({ id: environment.id, name: environment.name })),
    [environments]
  )
  const providerOptionsRef = useRef<QueryFilterOption[]>([])
  const botNameOptionsRef = useRef<QueryFilterOption[]>([])

  const botNameOptions = useMemo(() => {
    const eventBotNames = events.flatMap((event) => (event.bot_info?.name ? [event.bot_info.name] : []))
    botNameOptionsRef.current = mergeRememberedOptions(
      botNameOptionsRef.current,
      withBotNameIcons(
        mergeSelectedOptions(uniqueOptions([...KNOWN_BOT_NAMES, ...eventBotNames]), filterValues.botName)
      )
    )
    return botNameOptionsRef.current
  }, [events, filterValues.botName])

  const providerOptions = useMemo(() => {
    const knownProviderSlugs = BOT_PROVIDER_DEFINITIONS.map((definition) => definition.slug)
    const eventProviderSlugs = events.flatMap((event) => (event.bot_info?.provider ? [event.bot_info.provider] : []))

    providerOptionsRef.current = mergeRememberedOptions(
      providerOptionsRef.current,
      withProviderLabelsAndIcons(
        mergeSelectedOptions(uniqueOptions([...knownProviderSlugs, ...eventProviderSlugs]), filterValues.provider)
      )
    )

    return providerOptionsRef.current
  }, [events, filterValues.provider])

  const filterDefinitions = useMemo<
    Record<BotDetectionEventsFilterKey, QueryFilterDefinition<BotDetectionEventsFilterValues>>
  >(() => {
    return {
      botDetected: {
        id: 'botDetected',
        label: 'Bot detected',
        inputType: 'boolean',
        value: filterValues.botDetected,
        tooltip: 'Find events by whether a bot was detected.',
        icon: <Focus size={16} />,
      },
      botName: {
        id: 'botName',
        label: 'Bot name',
        inputType: 'checklist',
        value: filterValues.botName ?? [],
        options: botNameOptions,
        searchableOptions: true,
        allowCustomValue: true,
        placeholder: 'Filter or enter bot name',
        tooltip: 'Find events by bot name.',
        icon: <Bot size={16} />,
      },
      category: {
        id: 'category',
        label: 'Category',
        inputType: 'checklist',
        value: filterValues.category ?? [],
        options: categoryOptions,
        searchableOptions: true,
        placeholder: 'Filter category',
        tooltip: 'Find events by bot category.',
        icon: <Grid2X2 size={16} />,
      },
      provider: {
        id: 'provider',
        label: 'Provider',
        inputType: 'checklist',
        value: filterValues.provider ?? [],
        options: providerOptions,
        searchableOptions: true,
        allowCustomValue: true,
        placeholder: 'Filter or enter provider',
        tooltip: 'Find events by bot provider.',
        icon: <IdCardLanyardIcon size={16} />,
      },
      identity: {
        id: 'identity',
        label: 'Identity',
        inputType: 'checklist',
        value: filterValues.identity ?? [],
        options: identityOptions,
        searchableOptions: true,
        placeholder: 'Filter identity',
        tooltip: 'Find events by bot identity.',
        icon: <Fingerprint size={16} />,
      },
      confidence: {
        id: 'confidence',
        label: 'Confidence',
        inputType: 'checklist',
        value: filterValues.confidence ?? [],
        options: confidenceOptions,
        searchableOptions: true,
        placeholder: 'Filter confidence',
        tooltip: 'Find events by bot confidence level.',
        icon: <SignalFilled size={16} filledBars={3} />,
      },
      ipAddress: {
        id: 'ipAddress',
        label: 'IP address',
        inputType: 'text',
        value: filterValues.ipAddress ?? '',
        placeholder: '127.0.0.1',
        description: 'Press Enter to apply or Esc to cancel',
        tooltip: 'Find events originating from the specified IP address.',
        icon: <Globe size={16} />,
      },
      proxy: {
        id: 'proxy',
        label: 'Proxy',
        inputType: 'boolean',
        value: filterValues.proxy,
        tooltip: 'Find events by proxy signal status.',
        icon: <ServerIcon size={16} />,
      },
      vpn: {
        id: 'vpn',
        label: 'VPN',
        inputType: 'boolean',
        value: filterValues.vpn,
        tooltip: 'Find events by VPN signal status.',
        icon: <GlobeLockIcon size={16} />,
      },
      environmentIds: {
        id: 'environmentIds',
        label: 'Environment',
        inputType: 'checklist',
        value: filterValues.environmentIds ?? [],
        options: environmentOptions,
        searchableOptions: true,
        placeholder: 'Filter environment',
        tooltip: 'Find events by environment.',
        icon: <Box size={16} />,
      },
    }
  }, [botNameOptions, filterValues, providerOptions, environmentOptions])

  const filterMenuLayout = useMemo<QueryFilterMenuSection<BotDetectionEventsFilterValues>[]>(
    () => [
      {
        id: 'bot',
        items: [
          filterDefinitions.botDetected,
          filterDefinitions.botName,
          filterDefinitions.category,
          filterDefinitions.provider,
          filterDefinitions.identity,
          filterDefinitions.confidence,
        ],
      },
      {
        id: 'network',
        items: [filterDefinitions.ipAddress, filterDefinitions.proxy, filterDefinitions.vpn],
      },
      {
        id: 'environment',
        items: [filterDefinitions.environmentIds],
      },
    ],
    [filterDefinitions]
  )

  return { filterDefinitions, filterMenuLayout }
}
